/**
 * Pure logic for @-mention tokens in the AI Image prompt editor.
 *
 * Tokens are ALWAYS the English plain text `@Image <N>` regardless of UI
 * locale (one regex, no locale-aware parsing), where N is the 1-based
 * position of the image in `state.aiImage.generateForm.refImages`. The token
 * exists only in the display text — at Generate time it resolves to
 * `reference image <N>` (see resolveMentionsForRequest, applied in
 * store/features/aiImage/utils.ts::prepareGenerationPayload), which is what
 * the AI providers understand.
 *
 * Ported from ai-image-generator / ai-video-generator; keep the token grammar
 * identical across the three repos.
 */

export const MENTION_TOKEN_REGEX = /@Image (\d+)/g

/**
 * Extra characters a token gains when resolved for the request:
 * `reference image ` (16) minus `@Image ` (7). Character-limit checks must
 * budget this per mention — the backend limit applies to the RESOLVED text.
 */
export const MENTION_RESOLUTION_EXTRA_CHARS = 9

// Token plus at most one following space, so stripping a mention does not
// leave a double space behind.
const MENTION_TOKEN_WITH_SPACE_REGEX = /@Image (\d+) ?/g

/**
 * The RESOLVED wording, for turning a stored prompt back into tokens
 * (restoreMentionsFromResolved). Mirrors MENTION_TOKEN_WITH_SPACE_REGEX,
 * including the optional trailing space, so the round trip is exact.
 * Deliberately case-sensitive: it matches only what the resolver emits.
 */
const RESOLVED_MENTION_WITH_SPACE_REGEX = /reference image (\d+) ?/g

export interface IMentionMatch {
  index: number
  length: number
  imageNumber: number
}

export interface IMentionQuery {
  /** Index of the trigger '@' in the text. */
  startIndex: number
  /** Digits typed between '@' and the caret (may be empty). */
  query: string
}

export type TMentionSegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; imageNumber: number }

/**
 * Split prompt text into alternating text/mention segments — the render
 * model for the chip-based prompt editor. Concatenating the segments back
 * (mentions as `@Image N`) reproduces the input text exactly.
 */
export const splitMentionSegments = (text: string): TMentionSegment[] => {
  const segments: TMentionSegment[] = []
  let cursor = 0

  for (const match of text.matchAll(MENTION_TOKEN_REGEX)) {
    if (match.index > cursor) {
      segments.push({ type: 'text', value: text.slice(cursor, match.index) })
    }
    segments.push({ type: 'mention', imageNumber: Number(match[1]) })
    cursor = match.index + match[0].length
  }

  if (cursor < text.length) {
    segments.push({ type: 'text', value: text.slice(cursor) })
  }

  return segments
}

/**
 * Find all mention tokens in the text.
 * @param text Prompt display text
 * @returns Matches in document order
 */
export const parseMentions = (text: string): IMentionMatch[] =>
  Array.from(text.matchAll(MENTION_TOKEN_REGEX), match => ({
    index: match.index,
    length: match[0].length,
    imageNumber: Number(match[1])
  }))

/**
 * The length a character limit must be checked against: the text as displayed
 * plus the growth every mention gains once resolved for the request. Because
 * chips expand (`@Image 1` -> `reference image 1`), a prompt that displays under
 * the limit can still exceed it in the body — so wherever the limit is the
 * backend's, budget with this instead of `text.length`.
 * @param text Prompt display text
 */
export const getResolvedLength = (text: string): number =>
  text.length + parseMentions(text).length * MENTION_RESOLUTION_EXTRA_CHARS

/**
 * Detect an active mention trigger at the caret.
 * The '@' must be at the start of the text or preceded by whitespace, and
 * everything between '@' and the caret must be digits (the filter query).
 * @param params.text Current textarea value
 * @param params.caretIndex Caret position (selectionStart)
 * @returns The active query, or null when the picker should be closed
 */
export const getMentionQuery = (params: {
  text: string
  caretIndex: number
}): IMentionQuery | null => {
  const { text, caretIndex } = params
  const beforeCaret = text.slice(0, caretIndex)
  const startIndex = beforeCaret.lastIndexOf('@')

  if (startIndex === -1) {
    return null
  }
  if (startIndex > 0 && !/\s/.test(beforeCaret[startIndex - 1])) {
    return null
  }

  const query = beforeCaret.slice(startIndex + 1)
  if (!/^\d*$/.test(query)) {
    return null
  }

  return { startIndex, query }
}

/**
 * Replace the active trigger ('@' + query) with a complete mention token
 * followed by a space.
 * @param params.mentionStartIndex Index of the '@' (from getMentionQuery)
 * @param params.caretIndex Current caret position (end of the query)
 * @returns New text + caret position, or null when the result would exceed
 *          maxLength (insertion is skipped entirely — a clamped token would
 *          be corrupt)
 */
export const insertMention = (params: {
  text: string
  mentionStartIndex: number
  caretIndex: number
  imageNumber: number
  maxLength?: number
}): { text: string; caretIndex: number } | null => {
  const { text, mentionStartIndex, caretIndex, imageNumber, maxLength } = params
  const token = `@Image ${imageNumber} `
  const nextText =
    text.slice(0, mentionStartIndex) + token + text.slice(caretIndex)

  if (maxLength && nextText.length > maxLength) {
    return null
  }

  return { text: nextText, caretIndex: mentionStartIndex + token.length }
}

/**
 * Rewrite every mention token through a remap function.
 * @param params.remap Returns the new image number, or null to strip the
 *                     token (together with one following space, if present)
 */
export const remapMentions = (params: {
  text: string
  remap: (imageNumber: number) => number | null
}): string => {
  const { text, remap } = params
  return text.replace(MENTION_TOKEN_WITH_SPACE_REGEX, (match, digits) => {
    const nextNumber = remap(Number(digits))
    if (nextNumber === null) {
      return ''
    }
    const trailingSpace = match.endsWith(' ') ? ' ' : ''
    return `@Image ${nextNumber}${trailingSpace}`
  })
}

/**
 * Rewrite mentions after the image at 1-based position removedNumber was
 * removed: its mentions are stripped, higher numbers shift down by one.
 */
export const renumberMentionsAfterRemoval = (
  text: string,
  removedNumber: number
): string =>
  remapMentions({
    text,
    remap: imageNumber => {
      if (imageNumber === removedNumber) {
        return null
      }
      return imageNumber > removedNumber ? imageNumber - 1 : imageNumber
    }
  })

/**
 * Strip mentions of images beyond keptCount (e.g. after a model switch
 * truncates the uploaded list). keptCount = 0 strips all mentions.
 */
export const stripMentionsAbove = (text: string, keptCount: number): string =>
  remapMentions({
    text,
    remap: imageNumber => (imageNumber > keptCount ? null : imageNumber)
  })

/**
 * Resolve display tokens into the provider-facing wording for the request
 * body: `@Image <display N>` -> `reference image <sent position>`. Tokens
 * whose image is not in the sent array are stripped.
 *
 * In v-editor nothing is filtered out of `refImages` on the way to the API, so
 * the two numberings coincide; the mapping is kept explicit anyway because the
 * sibling repos DO filter (uploading/errored entries), and so a future filter
 * here cannot silently misnumber every mention.
 *
 * @param params.sentDisplayNumbers 1-based refImages positions actually sent,
 *        in sent order (see prepareGenerationPayload)
 */
export const resolveMentionsForRequest = (params: {
  text: string
  sentDisplayNumbers: number[]
}): string => {
  const { text, sentDisplayNumbers } = params
  return text.replace(MENTION_TOKEN_WITH_SPACE_REGEX, (match, digits) => {
    const sentIndex = sentDisplayNumbers.indexOf(Number(digits))
    if (sentIndex === -1) {
      return ''
    }
    const trailingSpace = match.endsWith(' ') ? ' ' : ''
    return `reference image ${sentIndex + 1}${trailingSpace}`
  })
}

/**
 * Pair each SENT reference position with the display number that image has
 * after a reload, by matching urls.
 *
 * Needed because the stored prompt numbers by SENT position while a reloaded
 * form numbers by its own list, and the two differ whenever loading drops an
 * image — both tabs re-validate reference urls and discard the deleted ones
 * (`filterDeletedReferenceImages`, `filterValidImages`). Matching by url rather
 * than threading indices also covers duplicates collapsing onto one entry.
 *
 * Both lists must be normalized the same way by the caller — this module stays
 * dependency-free.
 *
 * @param params.sentUrls reference urls in the order the prompt refers to them
 * @param params.loadedUrls urls the form now shows, in display order
 * @returns for each 1-based sent position, its new display number; 0 when that
 *          image did not survive the reload
 */
export const mapSentPositionsToDisplayNumbers = (params: {
  sentUrls: readonly string[]
  loadedUrls: readonly string[]
}): number[] => {
  const { sentUrls, loadedUrls } = params

  return sentUrls.map(url => {
    const loadedIndex = loadedUrls.indexOf(url)

    return loadedIndex === -1 ? 0 : loadedIndex + 1
  })
}

/**
 * Inverse of resolveMentionsForRequest: turn the stored provider wording back
 * into display tokens, so a prompt reloaded from history renders as chips.
 *
 * A mention whose image did not survive the reload is left as the LITERAL words
 * — the reload must not silently delete part of the user's prompt.
 *
 * Only for prompts flowing into the EDITABLE form. Read-only prompt displays
 * (library cards, Version History) must keep showing the stored wording.
 *
 * @param params.displayNumberBySentPosition see mapSentPositionsToDisplayNumbers;
 *        pass an empty array to leave the text untouched (nothing mentionable)
 */
export const restoreMentionsFromResolved = (params: {
  text: string
  displayNumberBySentPosition: readonly number[]
}): string => {
  const { text, displayNumberBySentPosition } = params

  if (!displayNumberBySentPosition.length) {
    return text
  }

  return text.replace(RESOLVED_MENTION_WITH_SPACE_REGEX, (match, digits) => {
    const displayNumber = displayNumberBySentPosition[Number(digits) - 1]

    if (!displayNumber) {
      return match
    }

    const trailingSpace = match.endsWith(' ') ? ' ' : ''

    return `@Image ${displayNumber}${trailingSpace}`
  })
}
